import {
  forwardRef,
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
} from "react";
import { titleCase } from "./textUtils";
import { splitWineName } from "./wineText";
import {
  DEFAULT_LAYOUT,
  norm,
  splitKey,
  sectionConfig,
  resolveStyle,
  toCss,
  fontCss,
  assetSrc,
  MENU_ORDER,
} from "./layout/defaultLayout";
import "./MenuPrint.css";

/* ═══════════════════════════════════════════════════════════════════════════
   MenuPrint — printable (Letter) rendering of the CMS menus, driven by the
   layout document (layout/defaultLayout.js). Two modes:

   • print: rendered off-screen and handed to react-to-print.
   • design: the exact same tree, rendered inside the Menu Designer with an
     EditorContext — every meaningful element becomes a selectable Node,
     text becomes editable in place, sections/items drag, and free
     decorations get move/resize/rotate handles. What you see is what prints.
   ═══════════════════════════════════════════════════════════════════════════ */

export const EditorContext = createContext(null);
const useEditor = () => useContext(EditorContext);
// Group contexts: a part (item name, section title…) selects its group
// first — the item or section — and only on a second click itself.
const ItemContext = createContext(null);
const SectionContext = createContext(null);

const SCALABLE = new Set(["itemName", "itemDesc", "itemPrice", "sectionTitle", "sectionSub", "text"]);

// ─── Editable node wrapper ─────────────────────────────────────────────────

/* Wraps a printed element. In print mode it's a plain tag; in design mode
   it carries the node descriptor for selection, hover, and drag/drop. */
function Node({ node, as: Tag = "div", className = "", style, children, dnd, ...rest }) {
  const ed = useEditor();
  if (!ed) {
    return (
      <Tag className={className} style={style} {...rest}>
        {children}
      </Tag>
    );
  }
  const selected = ed.isSelected(node.id);
  const hover = ed.dropHover?.id === node.id ? ` mpe-drop-${ed.dropHover.where}` : "";
  const dragging = ed.dragSourceIds?.has(node.id) ? " mpe-dragging" : "";
  const scalable = SCALABLE.has(node.kind);
  const props = {
    className: `${className} mpe-node mpe-${node.kind}${selected ? " mpe-selected" : ""}${
      node.hidden ? " mpe-hidden" : ""
    }${scalable ? " mpe-scalable" : ""}${hover}${dragging}`,
    style,
    "data-node": node.id,
    onClick: (e) => {
      e.stopPropagation();
      // Click a part of an unselected group → the group. Click again → the part.
      if (node.group && !ed.isSelected(node.group.id) && !ed.isSelected(node.id)) ed.select(node.group, e);
      else ed.select(node, e);
    },
    ...rest,
  };
  // Corner handles on selected text: drag to scale the type size.
  const handles =
    scalable && selected
      ? ["nw", "ne", "sw", "se"].map((h) => (
          <span
            key={h}
            className={`mpe-handle mpe-handle--${h}`}
            onPointerDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              ed.beginTextScale(e, node, h);
            }}
          />
        ))
      : null;
  if (dnd) {
    // Pointer-based drag (no browser drag ghost, so the element keeps its
    // on-canvas size). The designer decides what a drag means: items move
    // freely, ⌥-drag reorders; sections always reorder / change page.
    props["data-dnd"] = JSON.stringify(dnd);
    props.onPointerDown = (e) => {
      if (e.button !== 0 || ed.editing || e.target.isContentEditable) return;
      e.stopPropagation();
      ed.beginNodeDrag(e, node, dnd);
    };
    if (dnd.type === "item")
      props.onContextMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        ed.openContextMenu(e, node);
      };
  }
  if (Tag === "img") return <Tag {...props} />;
  return (
    <Tag {...props}>
      {children}
      {handles}
    </Tag>
  );
}

/* Text that edits in place on double-click (design mode only). `target`
   tells the designer where the new value goes: a layout text key, a CMS
   item field, or a section title. */
function Editable({ target, value, as: Tag = "span", className = "", style, multiline }) {
  const ed = useEditor();
  const ref = useRef(null);
  const [editing, setEditing] = useState(false);
  const [rev, setRev] = useState(0);
  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      const sel = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(ref.current);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [editing]);
  if (!ed) {
    return (
      <Tag className={className} style={style}>
        {value}
      </Tag>
    );
  }
  const commit = () => {
    const v = (ref.current?.innerText ?? "").replace(/\n+$/, "");
    setEditing(false);
    ed.setEditing?.(false);
    setRev((r) => r + 1);
    if (v !== value) ed.commitText(target, multiline ? v : v.replace(/\s*\n\s*/g, " "));
  };
  return (
    <Tag
      key={rev}
      ref={ref}
      className={`${className} mpe-text${editing ? " mpe-editing" : ""}`}
      style={style}
      contentEditable={editing}
      suppressContentEditableWarning
      spellCheck={false}
      onDoubleClick={(e) => {
        e.stopPropagation();
        setEditing(true);
        ed.setEditing?.(true);
      }}
      onMouseDown={editing ? (e) => e.stopPropagation() : undefined}
      onClick={editing ? (e) => e.stopPropagation() : undefined}
      onDragStart={editing ? (e) => e.preventDefault() : undefined}
      onBlur={editing ? commit : undefined}
      onKeyDown={
        editing
          ? (e) => {
              e.stopPropagation();
              if (e.key === "Escape") {
                ref.current.innerText = value;
                ref.current.blur();
              } else if (e.key === "Enter" && (!multiline || e.metaKey)) {
                e.preventDefault();
                ref.current.blur();
              }
            }
          : undefined
      }
    >
      {value}
    </Tag>
  );
}

/* Layout-document text ("Est. 1993", footer, block titles…). */
function LayoutText({ textKey, as = "p", className = "", layout, multiline, label }) {
  const t = layout.text?.[textKey] || DEFAULT_LAYOUT.text[textKey] || {};
  const style = toCss(t.style);
  return (
    <Node
      node={{ id: `text:${textKey}`, kind: "text", textKey, label }}
      as={as}
      className={className}
      style={style}
    >
      <Editable value={t.value ?? ""} target={{ type: "text", textKey }} multiline={multiline} />
    </Node>
  );
}

/* Multi-row layout text: one <p> per line, each editable on its own. */
function LayoutRows({ textKey, className, layout, label }) {
  const t = layout.text?.[textKey] || DEFAULT_LAYOUT.text[textKey] || {};
  const style = toCss(t.style);
  const rows = String(t.value || "").split("\n");
  return (
    <Node
      node={{ id: `text:${textKey}`, kind: "text", textKey, label }}
      className={className}
      style={style}
    >
      {rows.map((r, i) => (
        <Editable
          key={`${i}-${r}`}
          as="p"
          value={r}
          target={{ type: "text", textKey, line: i }}
        />
      ))}
    </Node>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function fmtPrice(p, style) {
  const n = Number(p);
  if (!Number.isFinite(n)) return "";
  if (style === "wine") return Number.isInteger(n) ? String(n) : String(+n.toFixed(2));
  return n.toFixed(2);
}

function hasGlutenFree(item) {
  return [...(item.options || []), ...(item.pastas || [])].some((o) =>
    o.name?.toLowerCase().includes("gluten free")
  );
}

function sharedSizeNames(items) {
  const first = items.find((i) => i.sizes?.length > 1);
  if (!first) return null;
  const names = first.sizes.map((s) => s.name);
  const allSame = items.every(
    (i) => i.sizes?.length === names.length && i.sizes.every((s, k) => s.name === names[k])
  );
  return allSame ? names : null;
}

function priceText(item, { style, withSizeNames } = {}) {
  const prices = (item.price || []).filter((p) => Number.isFinite(Number(p)));
  if (prices.length === 0) return "market price";
  const sizes = item.sizes || [];
  if (withSizeNames && sizes.length === prices.length && prices.length > 1) {
    return prices
      .map((p, i) => `${String(sizes[i].name || "").toLowerCase()} ${fmtPrice(p, style)}`)
      .join(" • ");
  }
  return prices.map((p) => fmtPrice(p, style)).join(" • ");
}

function sharedPrice(items) {
  const prices = (items || []).map((i) => Number(i.price?.[0]));
  if (!prices.length || !prices.every((p) => Number.isFinite(p))) return null;
  return prices.every((p) => p === prices[0]) ? fmtPrice(prices[0]) : null;
}

/* Items to render: print drops hidden ones; the designer shows them dimmed. */
function useVisibleItems(section, layout) {
  const ed = useEditor();
  const items = section.items || [];
  if (ed) return items;
  return items.filter((i) => !layout.items?.[i._id]?.hidden);
}

function isHidden(layout, item) {
  return !!layout.items?.[item._id]?.hidden;
}

// ─── Decorations ───────────────────────────────────────────────────────────

function GfMark({ layout }) {
  const g = layout.theme?.gfMark || DEFAULT_LAYOUT.theme.gfMark;
  if (g.hidden) return null;
  return (
    <svg
      className="mp-gf-mark"
      viewBox="0 0 24 24"
      aria-label="gluten free"
      style={{ width: `${g.size}em`, height: `${g.size}em`, fill: g.color }}
    >
      {[0, 60, 120, 180, 240, 300].map((deg) => (
        <path
          key={deg}
          d="M12 12 C 10.4 9.4, 10.2 4.4, 12 1.4 C 13.8 4.4, 13.6 9.4, 12 12 Z"
          transform={`rotate(${deg} 12 12)`}
        />
      ))}
      <circle cx="12" cy="12" r="1.2" />
    </svg>
  );
}

/* Section-title ornament, one per side. All of its geometry is data. */
function Flourish({ side, sectionKey, layout, assets }) {
  const f = resolveStyle(layout, "flourish", { sectionKey });
  const src = assetSrc(f.asset, assets);
  if (f.hidden || !src) return null;
  const mirror = side === "r" && f.mirror !== false;
  const style = {
    height: `${f.height}in`,
    top: `${f.dy || 0}in`,
    opacity: f.opacity ?? 1,
    transform: `${mirror ? "scaleX(-1) " : ""}rotate(${f.rotate || 0}deg)`,
  };
  return (
    <Node
      node={{
        id: `flourish:${sectionKey}:${side}`,
        kind: "flourish",
        sectionKey,
        label: "Title swirl",
      }}
      as="img"
      className={`mp-flourish${side === "r" ? " mp-flourish--r" : ""}`}
      style={style}
      src={src}
      alt=""
      draggable={false}
    />
  );
}

/* Page-corner ornaments: outer-left (tl+bl), outer-right (tr+br), or all. */
function CornerOrnaments({ menu, pageIndex, pageCfg, layout, assets }) {
  const mode = pageCfg.corners || "none";
  if (mode === "none") return null;
  const c = { ...(layout.theme?.corner || {}), ...(pageCfg.corner || {}) };
  const src = assetSrc(c.asset, assets);
  if (c.hidden || !src) return null;
  const corners =
    mode === "left" ? ["tl", "bl"] : mode === "right" ? ["tr", "br"] : ["tl", "tr", "bl", "br"];
  const flip = {
    bl: "",
    br: "scaleX(-1)",
    tl: "scaleY(-1)",
    tr: "scale(-1, -1)",
  };
  return corners.map((k) => (
    <Node
      key={k}
      node={{ id: `corner:${menu}:${pageIndex}:${k}`, kind: "corner", menu, pageIndex, label: "Corner ornament" }}
      as="img"
      className={`mp-corner mp-corner--${k}`}
      style={{
        width: `${c.size}in`,
        opacity: c.opacity ?? 1,
        [k[0] === "t" ? "top" : "bottom"]: `${c.inset}in`,
        [k[1] === "l" ? "left" : "right"]: `${c.inset}in`,
        transform: `${flip[k]} rotate(${c.rotate || 0}deg)`.trim(),
      }}
      src={src}
      alt=""
      draggable={false}
    />
  ));
}

// ─── Free-floating elements (the Canva-style layer) ───────────────────────
// Each element is absolutely positioned on its page in inches. In design
// mode the box carries eight resize handles and a rotation stalk; locked
// elements ignore the pointer, hidden ones print nothing and show dimmed.

const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

function shadowCss(sh) {
  if (!sh || (!sh.blur && !sh.x && !sh.y)) return undefined;
  return `${sh.x || 0}pt ${sh.y || 0}pt ${sh.blur || 0}pt ${sh.color || "rgba(0,0,0,0.35)"}`;
}

function borderCss(b) {
  if (!b || !b.width) return undefined;
  return `${b.width}pt ${b.style || "solid"} ${b.color || "var(--mp-ink)"}`;
}

function FreeElement({ el, menu, pageIndex, layout, assets }) {
  const ed = useEditor();
  if (el.hidden && !ed) return null;
  const node = { id: `free:${el.id}`, kind: "free", menu, pageIndex, freeId: el.id, label: el.name || el.type, hidden: !!el.hidden, locked: !!el.locked };
  const selected = ed?.isSelected(node.id);
  const flips = `${el.flipH ? " scaleX(-1)" : ""}${el.flipV ? " scaleY(-1)" : ""}`;
  const isLine = el.type === "rule";
  const base = {
    position: "absolute",
    left: `${el.x}in`,
    top: `${el.y}in`,
    width: `${el.w}in`,
    height: isLine ? "auto" : el.type === "text" && el.overflow !== "truncate" && !el.h ? "auto" : `${el.h ?? 1}in`,
    opacity: el.opacity ?? 1,
    transform: `rotate(${el.rotate || 0}deg)${flips}`,
    transformOrigin: "center",
    boxShadow: el.type === "text" || isLine || el.type === "image" ? undefined : shadowCss(el.shadow),
    filter: el.type === "image" && shadowCss(el.shadow) ? `drop-shadow(${shadowCss(el.shadow)})` : undefined,
    background: el.type === "rect" || el.type === "ellipse" || el.type === "text" ? el.fill : undefined,
    border: el.type === "rect" || el.type === "ellipse" || el.type === "text" ? borderCss(el.border) : undefined,
    borderRadius: el.type === "ellipse" ? "50%" : el.type === "rect" && el.radius ? `${el.radius}pt` : undefined,
    pointerEvents: ed && el.locked ? "none" : undefined,
  };
  let content = null;
  if (el.type === "image") {
    const src = assetSrc(el.asset, assets);
    content = src ? <img src={src} alt="" draggable={false} className="mp-free-img" /> : <div className="mp-free-missing" />;
  } else if (el.type === "text") {
    const st = toCss(el.style) || {};
    if (el.style?.textShadow) st.textShadow = shadowCss(el.style.textShadow);
    if (el.overflow === "truncate") Object.assign(st, { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" });
    content = (
      <Editable
        as="p"
        className="mp-free-text"
        style={st}
        value={el.text || ""}
        target={{ type: "free", menu, pageIndex, freeId: el.id }}
        multiline
      />
    );
  } else if (isLine) {
    content = <div className="mp-free-rule" style={{ borderTop: `${el.thickness || 0.75}pt ${el.border?.style || "solid"} ${el.color || "var(--mp-ink)"}` }} />;
  }
  const handlers =
    ed && !el.locked
      ? {
          onPointerDown: (e) => {
            if (e.target.isContentEditable || e.button !== 0) return;
            e.stopPropagation();
            ed.pointerDownFree(e, node, { menu, pageIndex, freeId: el.id });
          },
          onContextMenu: (e) => {
            e.preventDefault();
            e.stopPropagation();
            ed.openContextMenu(e, node);
          },
        }
      : {};
  return (
    <Node node={node} className={`mp-free mp-free--${el.type}${el.locked ? " mpe-locked" : ""}`} style={base} {...handlers}>
      {content}
      {selected && !el.locked && (
        <>
          {(isLine ? ["e", "w"] : HANDLES).map((h) => (
            <span
              key={h}
              className={`mpe-handle mpe-handle--${h}`}
              onPointerDown={(e) => {
                e.stopPropagation();
                ed.beginFreeDrag(e, { menu, pageIndex, freeId: el.id, mode: "resize", handle: h });
              }}
            />
          ))}
          <span
            className="mpe-handle mpe-handle--rot"
            onPointerDown={(e) => {
              e.stopPropagation();
              ed.beginFreeDrag(e, { menu, pageIndex, freeId: el.id, mode: "rotate" });
            }}
          />
        </>
      )}
    </Node>
  );
}

function FreeLayer({ free, menu, pageIndex, layout, assets }) {
  if (!free?.length) return null;
  return free.map((el) => (
    <FreeElement key={el.id} el={el} menu={menu} pageIndex={pageIndex} layout={layout} assets={assets} />
  ));
}

// ─── Section building blocks ───────────────────────────────────────────────

function SectionTitle({ section, sectionKey, cfg, layout, assets, subtitle, className = "" }) {
  const group = useContext(SectionContext);
  const titleStyle = toCss(resolveStyle(layout, "sectionTitle", { sectionKey }));
  const subStyle = toCss(resolveStyle(layout, "sectionSub", { sectionKey }));
  const f = resolveStyle(layout, "flourish", { sectionKey });
  const label = cfg.title ?? section.header;
  return (
    <header className="mp-sec-head">
      <Node
        node={{ id: `sectionTitle:${sectionKey}`, kind: "sectionTitle", sectionKey, label: "Section title", group }}
        as="h2"
        className={`mp-sec-title ${className}`}
        style={{ ...(titleStyle || {}), gap: `${f.gap ?? 0.17}in` }}
      >
        <Flourish side="l" sectionKey={sectionKey} layout={layout} assets={assets} />
        <Editable
          value={label}
          target={{ type: "sectionTitle", sectionKey, sectionId: section._id, hasOverride: cfg.title != null }}
        />
        <Flourish side="r" sectionKey={sectionKey} layout={layout} assets={assets} />
      </Node>
      {subtitle && (
        <Node
          node={{ id: `sectionSub:${sectionKey}`, kind: "sectionSub", sectionKey, label: "Section note", group }}
          as="p"
          className="mp-sec-sub"
          style={subStyle}
        >
          {subtitle}
        </Node>
      )}
    </header>
  );
}

/* Section subtitle content: print note (with rosettes when items carry
   them) replaces the CMS description; pizza appends its size names. */
function subtitleFor({ section, cfg, layout, items, sizeNames }) {
  const desc = section.description ? (
    <Editable
      value={section.description}
      target={{ type: "section", sectionId: section._id, field: "description" }}
    />
  ) : null;
  if (cfg.note) {
    const noteEl = (
      <Editable value={cfg.note} target={{ type: "sectionNote", sectionKey: splitKey(cfg.__key).base }} />
    );
    return cfg.gfTags ? (
      <>
        <GfMark layout={layout} /> {noteEl} <GfMark layout={layout} />
      </>
    ) : (
      noteEl
    );
  }
  if (sizeNames) {
    return (
      <>
        {desc} {sizeNames.join(" • ")}
      </>
    );
  }
  return desc;
}

/* Item parts — name / description / price — each a selectable, editable node. */
function ItemName({ item, sectionKey, layout, className = "", gf, children, as = "h3", editTarget, text, printBinding }) {
  const group = useContext(ItemContext);
  const style = toCss(resolveStyle(layout, "itemName", { sectionKey, itemId: item._id }));
  return (
    <Node
      node={{ id: `itemName:${item._id}`, kind: "itemName", itemId: item._id, sectionKey, label: "Item name", group }}
      as={as}
      className={`mp-item-name ${className}`}
      data-cms-binding={printBinding}
      style={{ ...style, textTransform: "none" }}
    >
      {gf && <GfMark layout={layout} />}
      <Editable value={text ?? item.name} target={editTarget || { type: "item", itemId: item._id, field: "name" }} />
      {children}
    </Node>
  );
}

function ItemDesc({ item, sectionKey, layout, className = "", text, extra, as = "p" }) {
  const group = useContext(ItemContext);
  if (!item.description && !extra) return null;
  const style = toCss(resolveStyle(layout, "itemDesc", { sectionKey, itemId: item._id }));
  return (
    <Node
      node={{ id: `itemDesc:${item._id}`, kind: "itemDesc", itemId: item._id, sectionKey, label: "Item description", group }}
      as={as}
      className={`mp-item-desc ${className}`}
      style={style}
    >
      {item.description && (
        <Editable
          value={text ?? item.description}
          target={{ type: "item", itemId: item._id, field: "description" }}
        />
      )}
      {extra}
    </Node>
  );
}

function ItemPrice({ item, sectionKey, layout, className = "", text, as = "p" }) {
  const group = useContext(ItemContext);
  const style = toCss(resolveStyle(layout, "itemPrice", { sectionKey, itemId: item._id }));
  return (
    <Node
      node={{ id: `itemPrice:${item._id}`, kind: "itemPrice", itemId: item._id, sectionKey, label: "Item price", group }}
      as={as}
      className={`mp-item-price ${className}`}
      data-cms-binding={`item:${item._id}:price`}
      style={style}
    >
      <Editable value={text} target={{ type: "item", itemId: item._id, field: "price" }} />
    </Node>
  );
}

function ItemNode({ item, section, sectionKey, layout, className = "", children, style: extraStyle, as = "div" }) {
  const style = { ...(toCss(resolveStyle(layout, "item", { sectionKey, itemId: item._id })) || {}), ...(extraStyle || {}) };
  const node = { id: `item:${item._id}`, kind: "item", itemId: item._id, sectionId: section._id, sectionKey, label: titleCase(item.name), hidden: isHidden(layout, item) };
  return (
    <Node node={node} as={as} className={`mp-item ${className}`} style={style} dnd={{ type: "item", itemId: item._id, sectionId: section._id }}>
      <ItemContext.Provider value={node}>{children}</ItemContext.Provider>
    </Node>
  );
}

function bestSellerLabel(layout, item) {
  const over = layout.items?.[item._id]?.badge;
  if (over !== undefined) return over || null;
  const bs = layout.text?.bestSeller?.value || "";
  return bs && norm(item.name) === norm(bs) ? layout.text?.bestSellerLabel?.value || "#1 best seller" : null;
}

function Badge({ label }) {
  if (!label) return null;
  const m = /^(#\S+)\s*(.*)$/.exec(label);
  return (
    <p className="mp-bestseller">
      {m ? (
        <>
          <span className="mp-bestseller-no">{m[1]}</span> {m[2]}
        </>
      ) : (
        label
      )}
    </p>
  );
}

function CenteredItem({ item, section, sectionKey, layout, style, withSizeNames, gf }) {
  return (
    <ItemNode item={item} section={section} sectionKey={sectionKey} layout={layout}>
      <Badge label={bestSellerLabel(layout, item)} />
      <ItemName item={item} sectionKey={sectionKey} layout={layout} gf={gf} />
      <ItemDesc item={item} sectionKey={sectionKey} layout={layout} />
      <ItemPrice item={item} sectionKey={sectionKey} layout={layout} text={priceText(item, { style, withSizeNames })} />
    </ItemNode>
  );
}

function PlatterItem({ item, section, sectionKey, layout }) {
  const per = item.price?.[0];
  return (
    <ItemNode item={item} section={section} sectionKey={sectionKey} layout={layout} className="mp-platter">
      <ItemName item={item} sectionKey={sectionKey} layout={layout} />
      <ItemDesc item={item} sectionKey={sectionKey} layout={layout} />
      <ItemPrice
        item={item}
        sectionKey={sectionKey}
        layout={layout}
        text={Number.isFinite(Number(per)) ? `$${fmtPrice(per, "wine")} per selection of` : ""}
      />
      <ul className="mp-platter-list">
        {item.platters.map((p, i) => (
          <li key={i}>
            {String(p.name || "").toLowerCase()}
            {i < item.platters.length - 1 ? "," : ""}
          </li>
        ))}
      </ul>
    </ItemNode>
  );
}

// ─── Section layouts ───────────────────────────────────────────────────────

function CenteredSection({ section, sectionKey, cfg, style, layout, assets }) {
  const items = useVisibleItems(section, layout);
  const regular = items.filter((i) => !i.platters?.length);
  const platters = items.filter((i) => i.platters?.length);
  const sizeNames = cfg.showSizes ? sharedSizeNames(items) : null;
  const subtitle = subtitleFor({ section, cfg, layout, items, sizeNames });
  const cols = cfg.columns || 1;
  return (
    <>
      <SectionTitle section={section} sectionKey={sectionKey} cfg={cfg} layout={layout} assets={assets} subtitle={subtitle} />
      <div className={cols > 1 ? "mp-cols" : undefined} style={cols > 1 ? { gridTemplateColumns: `repeat(${cols}, 1fr)` } : undefined}>
        {regular.map((item) => (
          <CenteredItem
            key={item._id}
            item={item}
            section={section}
            sectionKey={sectionKey}
            layout={layout}
            style={style}
            withSizeNames={!sizeNames}
            gf={cfg.gfTags && hasGlutenFree(item)}
          />
        ))}
      </div>
      {platters.length > 0 && (
        <div className="mp-platters">
          {platters.map((item) => (
            <PlatterItem key={item._id} item={item} section={section} sectionKey={sectionKey} layout={layout} />
          ))}
        </div>
      )}
    </>
  );
}

function ListSection({ section, sectionKey, cfg, style, layout, assets }) {
  const items = useVisibleItems(section, layout);
  const anyGf = items.some(hasGlutenFree);
  const subtitle = section.description ? (
    <>
      {anyGf && <GfMark layout={layout} />}{" "}
      <Editable value={section.description} target={{ type: "section", sectionId: section._id, field: "description" }} />
    </>
  ) : cfg.note ? (
    subtitleFor({ section, cfg, layout, items })
  ) : null;
  return (
    <>
      <SectionTitle section={section} sectionKey={sectionKey} cfg={cfg} layout={layout} assets={assets} subtitle={subtitle} />
      {items.map((item) => (
        <ItemNode key={item._id} item={item} section={section} sectionKey={sectionKey} layout={layout} className="mp-li">
          <span className="mp-li-mark">{hasGlutenFree(item) && <GfMark layout={layout} />}</span>
          <div className="mp-li-body">
            <ItemName item={item} sectionKey={sectionKey} layout={layout} />
            <p className="mp-li-line">
              <ItemDesc as="span" item={item} sectionKey={sectionKey} layout={layout} />
              <ItemPrice as="span" item={item} sectionKey={sectionKey} layout={layout} className="mp-li-price" text={` • ${priceText(item, { style, withSizeNames: true })}`} />
            </p>
          </div>
        </ItemNode>
      ))}
    </>
  );
}

function InlineSection({ section, sectionKey, cfg, style, layout, assets }) {
  const items = useVisibleItems(section, layout);
  // Soda fountain: a comma-separated name prints as a heading with its
  // choices listed underneath, last in the section.
  const isSoda = (item) =>
    cfg.sodaFountain && String(item.name).split(",").filter((p) => p.trim()).length >= 3;
  const ordered = [...items.filter((i) => !isSoda(i)), ...items.filter(isSoda)];
  const subtitle = subtitleFor({ section, cfg, layout, items });
  return (
    <>
      <SectionTitle section={section} sectionKey={sectionKey} cfg={cfg} layout={layout} assets={assets} subtitle={subtitle} />
      {ordered.map((item) => {
        const soda = isSoda(item);
        const parts = String(item.name).split(",").map((p) => p.trim()).filter(Boolean);
        // The soda heading is print wording (layout), not the CMS item name.
        const label = soda ? cfg.sodaLabel || "Soda Fountain" : titleCase(item.name);
        return (
          <ItemNode key={item._id} item={item} section={section} sectionKey={sectionKey} layout={layout} className="mp-inline-item">
            <p className="mp-inline">
              <ItemName
                as="span"
                item={{ ...item, name: label }}
                sectionKey={sectionKey}
                layout={layout}
                className="mp-inline-name"
                editTarget={soda ? { type: "sectionCfg", sectionKey: splitKey(sectionKey).base, field: "sodaLabel" } : undefined}
              />
              {" • "}
              <ItemPrice as="span" item={item} sectionKey={sectionKey} layout={layout} className="mp-inline-price" text={priceText(item, { style, withSizeNames: !soda })} />
            </p>
            {soda && (
              <p className="mp-inline-sub">
                <Editable
                  value={parts.map((p) => titleCase(p).replace(/\bRc\b/, "RC")).join(" • ")}
                  target={{ type: "sodaList", itemId: item._id }}
                />
              </p>
            )}
          </ItemNode>
        );
      })}
    </>
  );
}

function WineHead({ section, sectionKey, cfg, layout }) {
  const group = useContext(SectionContext);
  const style = toCss(resolveStyle(layout, "sectionTitle", { sectionKey }));
  return (
    <Node
      node={{ id: `sectionTitle:${sectionKey}`, kind: "sectionTitle", sectionKey, label: "Section title", group }}
      as="h2"
      className="mp-wine-head"
      style={style}
    >
      <Editable
        value={cfg.title ?? section.header}
        target={{ type: "sectionTitle", sectionKey, sectionId: section._id, hasOverride: cfg.title != null }}
      />
    </Node>
  );
}

function BeerSection({ section, sectionKey, cfg, layout }) {
  const items = useVisibleItems(section, layout);
  return (
    <>
      <WineHead section={section} sectionKey={sectionKey} cfg={cfg} layout={layout} />
      {items.map((item) => (
        <ItemNode key={item._id} item={item} section={section} sectionKey={sectionKey} layout={layout} className="mp-beer-line">
          <ItemName item={item} sectionKey={sectionKey} layout={layout} className="mp-wine-name" printBinding={`item:${item._id}:name`}>
            <span className="mp-item-price mp-beer-separator"> • </span>
            <ItemPrice as="span" item={item} sectionKey={sectionKey} layout={layout} className="mp-beer-price" text={priceText(item, { style: "wine" })} />
          </ItemName>
        </ItemNode>
      ))}
    </>
  );
}

function WineSection({ section, sectionKey, cfg, layout }) {
  const items = useVisibleItems(section, layout);
  return (
    <>
      <WineHead section={section} sectionKey={sectionKey} cfg={cfg} layout={layout} />
      {items.map((item) => {
        const wine = splitWineName(item.name);
        return (
          <ItemNode key={item._id} item={item} section={section} sectionKey={sectionKey} layout={layout} className="mp-wine">
            <ItemName item={item} sectionKey={sectionKey} layout={layout} className="mp-wine-name" text={wine.label} printBinding={`item:${item._id}:wineLabel`} />
            {wine.origin && <p className="mp-wine-origin" data-cms-binding={`item:${item._id}:wineOrigin`}>{wine.origin}</p>}
            <ItemPrice item={item} sectionKey={sectionKey} layout={layout} className="mp-wine-price" text={priceText(item, { style: "wine" })} />
          </ItemNode>
        );
      })}
    </>
  );
}

// ─── Lunch layouts ─────────────────────────────────────────────────────────

function LunchHead({ section, sectionKey, cfg, layout, assets, price }) {
  const subtitle = (
    <>
      {section.description && (
        <Editable value={section.description} target={{ type: "section", sectionId: section._id, field: "description" }} />
      )}
      {section.description && price ? <br /> : null}
      {price}
    </>
  );
  return (
    <SectionTitle
      section={section}
      sectionKey={sectionKey}
      cfg={cfg}
      layout={layout}
      assets={assets}
      className="mp-lunch-sec-title"
      subtitle={section.description || price ? subtitle : null}
    />
  );
}

function LunchCentered({ section, sectionKey, cfg, layout, assets }) {
  const items = useVisibleItems(section, layout);
  const price = sharedPrice(items);
  return (
    <>
      <LunchHead section={section} sectionKey={sectionKey} cfg={cfg} layout={layout} assets={assets} price={price} />
      {items.map((item) => (
        <ItemNode key={item._id} item={item} section={section} sectionKey={sectionKey} layout={layout}>
          <ItemName item={item} sectionKey={sectionKey} layout={layout} />
          {(item.description || !price) && (
            <ItemDesc
              item={item}
              sectionKey={sectionKey}
              layout={layout}
              extra={
                price ? null : (
                  <ItemPrice as="span" item={item} sectionKey={sectionKey} layout={layout} text={`${item.description ? " • " : ""}${priceText(item, {})}`} />
                )
              }
            />
          )}
        </ItemNode>
      ))}
    </>
  );
}

function LunchGrid({ section, sectionKey, cfg, layout, assets }) {
  const items = useVisibleItems(section, layout);
  const price = sharedPrice(items);
  const cols = cfg.columns || 2;
  return (
    <>
      <LunchHead section={section} sectionKey={sectionKey} cfg={cfg} layout={layout} assets={assets} price={price} />
      <div
        className={`mp-lunch-grid mp-lunch-grid-2${cfg.tight ? " mp-lunch-grid-tight" : ""}`}
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
      >
        {items.map((item) => (
          <ItemNode key={item._id} item={item} section={section} sectionKey={sectionKey} layout={layout} className="mp-lunch-item">
            <ItemName item={item} sectionKey={sectionKey} layout={layout} />
            <ItemDesc item={item} sectionKey={sectionKey} layout={layout} />
            {!price && <ItemPrice item={item} sectionKey={sectionKey} layout={layout} text={priceText(item, {})} />}
          </ItemNode>
        ))}
      </div>
    </>
  );
}

function LunchInlineNames({ section, sectionKey, cfg, layout, assets }) {
  const items = useVisibleItems(section, layout);
  return (
    <>
      <LunchHead section={section} sectionKey={sectionKey} cfg={cfg} layout={layout} assets={assets} price={sharedPrice(items)} />
      <p className="mp-lunch-names">
        {items.map((item, i) => (
          <span key={item._id}>
            <ItemNode as="span" item={item} section={section} sectionKey={sectionKey} layout={layout} className="mp-lunch-name-item" style={{ display: "inline" }}>
              <ItemName as="span" item={item} sectionKey={sectionKey} layout={layout} className="mp-lunch-name" />
            </ItemNode>
            {i < items.length - 1 && <span className="mp-lunch-dot">{" • "}</span>}
          </span>
        ))}
      </p>
    </>
  );
}

const CLOCK = [
  { row: 1, col: "1 / 3" },
  { row: 1, col: "5 / 7" },
  { row: 2, col: "2 / 4" },
  { row: 2, col: "4 / 6" },
];
const CLOCK_ORDER = ['9" PIZZA', "PASTA OF THE DAY", "HALF SPECIALTY SANDWICH", "HOUSE SALAD AND BOWL", "BASKET OF BREAD"];

function LunchClock({ section, sectionKey, cfg, layout, assets }) {
  const items = useVisibleItems(section, layout);
  if (items.length < 3 || items.length > CLOCK.length + 1)
    return <LunchRows section={section} sectionKey={sectionKey} cfg={cfg} layout={layout} assets={assets} />;
  const placed = CLOCK_ORDER.map((key) => items.find((i) => norm(i.name).startsWith(key)));
  const rest = items.filter((i) => !placed.includes(i)).sort((x, y) => String(x.name).length - String(y.name).length);
  const ordered = placed.map((i) => i || rest.shift()).filter(Boolean);
  const [hub, ...around] = [...ordered, ...rest];
  return (
    <>
      <LunchHead section={section} sectionKey={sectionKey} cfg={cfg} layout={layout} assets={assets} />
      <div className="mp-lunch-clock">
        <ItemNode item={hub} section={section} sectionKey={sectionKey} layout={layout} className="mp-lunch-hub" style={{ gridRow: 1, gridColumn: "3 / 5" }}>
          <ItemName item={{ ...hub, name: titleCase(hub.name) }} sectionKey={sectionKey} layout={layout} className="mp-lunch-hub-name" />
          <ItemPrice item={hub} sectionKey={sectionKey} layout={layout} text={priceText(hub, {})} />
        </ItemNode>
        {around.map((item, i) => (
          <ItemNode key={item._id} item={item} section={section} sectionKey={sectionKey} layout={layout} className="mp-lunch-sat" style={{ gridRow: CLOCK[i].row, gridColumn: CLOCK[i].col }}>
            <ItemName item={{ ...item, name: titleCase(item.name) }} sectionKey={sectionKey} layout={layout} />
            <ItemPrice item={item} sectionKey={sectionKey} layout={layout} text={priceText(item, {})} />
          </ItemNode>
        ))}
      </div>
    </>
  );
}

function LunchRows({ section, sectionKey, cfg, layout, assets }) {
  const items = useVisibleItems(section, layout);
  return (
    <>
      <LunchHead section={section} sectionKey={sectionKey} cfg={cfg} layout={layout} assets={assets} />
      {items.map((item) => (
        <ItemNode key={item._id} item={item} section={section} sectionKey={sectionKey} layout={layout} className="mp-inline-item">
          <p className="mp-inline">
            <ItemName as="span" item={{ ...item, name: titleCase(item.name) }} sectionKey={sectionKey} layout={layout} className="mp-inline-name" />
            {" • "}
            <ItemPrice as="span" item={item} sectionKey={sectionKey} layout={layout} className="mp-inline-price" text={priceText(item, {})} />
          </p>
        </ItemNode>
      ))}
    </>
  );
}

// ─── Fixed blocks ──────────────────────────────────────────────────────────

function BuildYourOwn({ layout, assets }) {
  const f = resolveStyle(layout, "flourish", { sectionKey: "PIZZA" });
  return (
    <Node node={{ id: "block:byo", kind: "block", blockKey: "byo", label: "Build Your Own Pizza" }} as="section" className="mp-sec mp-block" style={toCss(layout.blocks?.byo)}>
      <header className="mp-sec-head">
        <h2 className="mp-sec-title" style={{ ...(toCss(layout.theme?.sectionTitle) || {}), gap: `${f.gap ?? 0.17}in` }}>
          <Flourish side="l" sectionKey="PIZZA" layout={layout} assets={assets} />
          <LayoutText textKey="byoTitle" as="span" layout={layout} label="Build Your Own · title" />
          <Flourish side="r" sectionKey="PIZZA" layout={layout} assets={assets} />
        </h2>
        <LayoutText textKey="byoSubtitle" className="mp-sec-sub" layout={layout} label="Build Your Own · subtitle" />
      </header>
      <LayoutRows textKey="byoRows" className="mp-byo-rows" layout={layout} label="Build Your Own · price rows" />
      <LayoutText textKey="byoToppings" className="mp-byo-toppings" layout={layout} multiline label="Build Your Own · toppings" />
    </Node>
  );
}

function PastaMods({ layout }) {
  return (
    <Node node={{ id: "block:pastaMods", kind: "block", blockKey: "pastaMods", label: "Make It Your Own" }} as="section" className="mp-sec mp-pasta-mods mp-block" style={toCss(layout.blocks?.pastaMods)}>
      <LayoutText textKey="modsTitle" className="mp-mods-title" layout={layout} label="Make It Your Own · title" />
      <LayoutRows textKey="modsRows" className="mp-mods-rows" layout={layout} label="Make It Your Own · rows" />
    </Node>
  );
}

const AFTER = { byo: BuildYourOwn, pastaMods: PastaMods };

// ─── Section dispatcher ────────────────────────────────────────────────────

const RENDERERS = {
  centered: CenteredSection,
  list: ListSection,
  inline: InlineSection,
  wine: WineSection,
  beer: BeerSection,
  lunchCentered: LunchCentered,
  lunchGrid: LunchGrid,
  lunchNames: LunchInlineNames,
  lunchClock: LunchClock,
  lunchRows: LunchRows,
};

function Section({ section, sectionKey, menu, pageIndex, layout, assets }) {
  const ed = useEditor();
  const cfg = { ...sectionConfig(layout, sectionKey, section.__menuType), __key: sectionKey };
  if (cfg.hidden && !ed) return null;
  const Renderer = RENDERERS[cfg.layout] || CenteredSection;
  const isWineLayout = cfg.layout === "wine" || cfg.layout === "beer";
  const cls = [
    "mp-sec",
    cfg.airy ? "mp-sec--airy" : "",
    cfg.layout === "list" ? "mp-sec--list" : "",
    cfg.layout === "inline" || cfg.layout === "lunchRows" ? "mp-sec--inline" : "",
    cfg.layout === "lunchRows" ? "mp-lunch-inline" : "",
    cfg.layout === "lunchCentered" ? "mp-lunch-centered" : "",
    isWineLayout ? "mp-sec--wine" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const secStyle = resolveStyle(layout, "section", { sectionKey });
  const style = toCss(secStyle);
  const After = cfg.after ? AFTER[cfg.after] : null;
  const priceStyle = section.__menuType === "wine" || isWineLayout ? "wine" : section.__menuType;
  const node = { id: `section:${sectionKey}`, kind: "section", sectionKey, sectionId: section._id, menu, pageIndex, label: titleCase(cfg.title ?? section.header), hidden: !!cfg.hidden };
  return (
    <>
      <Node
        node={node}
        as="section"
        className={cls}
        style={{ ...(style || {}), ...(secStyle.headGap != null ? { "--mp-head-gap": `${secStyle.headGap}in` } : {}), ...(secStyle.itemGap != null ? { "--mp-item-gap": `${secStyle.itemGap}in` } : {}) }}
        dnd={{ type: "section", sectionKey, menu, pageIndex }}
      >
        <SectionContext.Provider value={node}>
          <Renderer section={section} sectionKey={sectionKey} cfg={cfg} style={priceStyle} layout={layout} assets={assets} />
        </SectionContext.Provider>
      </Node>
      {After && <After layout={layout} assets={assets} />}
    </>
  );
}

// ─── Page furniture ────────────────────────────────────────────────────────

function BevHeader({ layout }) {
  return (
    <header className="mp-sec-head mp-bev-head">
      <LayoutText textKey="beverageTitle" as="h2" className="mp-bev-title" layout={layout} label="Wine & Beer title" />
      <LayoutText textKey="beverageSub" className="mp-sec-sub" layout={layout} label="Wine & Beer subtitle" />
    </header>
  );
}

function LunchMasthead({ layout }) {
  return (
    <div className="mp-lunch-mast">
      <LayoutText textKey="lunchTitle" as="h1" className="mp-lunch-title" layout={layout} label="Lunch title" />
      <LayoutText textKey="lunchNote" className="mp-lunch-note" layout={layout} label="Lunch service line" />
    </div>
  );
}

function CoverPage({ layout, assets }) {
  const ed = useEditor();
  const cover = layout.cover || DEFAULT_LAYOUT.cover;
  if (cover.hidden && !ed) return null;
  const logo = cover.logo || DEFAULT_LAYOUT.cover.logo;
  const src = assetSrc(logo.asset, assets);
  return (
    <Node
      node={{ id: "page:cover:0", kind: "page", menu: "cover", pageIndex: 0, label: "Cover", hidden: !!cover.hidden }}
      className={`mp-page mp-page--cover${ed?.grid ? " mpe-grid" : ""}`}
      style={{ ...(toCss(layout.theme?.page) || {}), ...(toCss(cover.page) || {}) }}
      data-dnd={JSON.stringify({ type: "page", menu: "cover", pageIndex: 0 })}
      onPointerDown={
        ed
          ? (e) => {
              if (e.button !== 0 || e.target.closest?.("[data-node]") !== e.currentTarget) return;
              ed.beginMarquee(e, { id: "page:cover:0", kind: "page", menu: "cover", pageIndex: 0, label: "Cover" });
            }
          : undefined
      }
    >
      {src && !logo.hidden && (
        <Node
          node={{ id: "logo", kind: "logo", label: "Cover logo" }}
          as="img"
          className="mp-cover-logo"
          style={{ width: `${logo.width}in`, marginTop: `${logo.marginTop ?? 0}in`, opacity: logo.opacity ?? 1, transform: logo.rotate ? `rotate(${logo.rotate}deg)` : undefined }}
          src={src}
          alt="Trattoria Demi"
          draggable={false}
        />
      )}
      <LayoutText textKey="established" className="mp-cover-script" layout={layout} label="Cover · Est. line" />
      <LayoutText textKey="footerNote" className="mp-cover-note" layout={layout} label="Cover · footer note" />
      <FreeLayer free={cover.free} menu="cover" pageIndex={0} layout={layout} assets={assets} />
    </Node>
  );
}

function Page({ menu, pageIndex, pageCfg, secs, layout, assets }) {
  const ed = useEditor();
  const p = { ...(layout.theme?.page || {}), ...(pageCfg.page || {}) };
  const masthead = pageCfg.masthead || "";
  const inset = masthead === "bev" || masthead === "lunch" || (pageCfg.corners && pageCfg.corners !== "none");
  const cls = [
    "mp-page",
    masthead === "lunch" ? "mp-page--lunch" : "",
    inset ? "mp-page--wine" : "",
    p.distribute === "flex-start" && masthead !== "lunch" ? "mp-page--top" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const style = { ...(toCss(p) || {}) };
  if (p.sectionGap != null) style["--mp-sec-gap"] = `${p.sectionGap}in`;
  if (p.paper) style.background = p.paper;
  const node = { id: `page:${menu}:${pageIndex}`, kind: "page", menu, pageIndex, label: `${titleCase(menu)} · page ${pageIndex + 1}` };
  const pageProps = ed
    ? {
        onPointerDown: (e) => {
          // Blank page space: a drag draws a selection marquee; a click
          // (no movement) selects the page.
          if (e.button !== 0 || e.target.closest?.("[data-node]") !== e.currentTarget) return;
          ed.beginMarquee(e, node);
        },
      }
    : {};
  return (
    <Node node={node} className={`${cls}${ed?.grid ? " mpe-grid" : ""}`} style={style} data-dnd={JSON.stringify({ type: "page", menu, pageIndex })} {...pageProps}>
      <CornerOrnaments menu={menu} pageIndex={pageIndex} pageCfg={pageCfg} layout={layout} assets={assets} />
      {masthead === "bev" && <BevHeader layout={layout} />}
      {masthead === "lunch" && <LunchMasthead layout={layout} />}
      {secs.map(({ key, section }) => (
        <Section key={key} section={section} sectionKey={key} menu={menu} pageIndex={pageIndex} layout={layout} assets={assets} />
      ))}
      {ed && secs.length === 0 && <p className="mpe-empty-page">Empty page — drag a section here</p>}
      <FreeLayer free={pageCfg.free} menu={menu} pageIndex={pageIndex} layout={layout} assets={assets} />
    </Node>
  );
}

// ─── Pagination ────────────────────────────────────────────────────────────

/* All sections of a menu keyed by normalized header, with multi-part
   sections (pasta) split into KEY, KEY#2, … Beer lives on the dinner menu
   but prints with the beverages, so the wine map includes it. */
function sectionMap(menus, type, layout) {
  const out = new Map();
  const add = (sec, menuType) => {
    const key = norm(sec.header);
    const parts = layout.sections?.[key]?.parts || 1;
    const items = sec.items || [];
    const tagged = { ...sec, __menuType: menuType };
    if (parts <= 1 || items.length < parts) {
      out.set(key, tagged);
      return;
    }
    const per = Math.ceil(items.length / parts);
    for (let i = 0; i < parts; i++) {
      const k = i === 0 ? key : `${key}#${i + 1}`;
      out.set(k, { ...tagged, _id: i === 0 ? sec._id : `${sec._id}-p${i + 1}`, items: items.slice(i * per, (i + 1) * per) });
    }
  };
  for (const sec of menus?.[type]?.sections || []) add(sec, type);
  if (type === "wine")
    for (const sec of menus?.dinner?.sections || []) if (norm(sec.header) === "BEER") add(sec, "dinner");
  return out;
}

/* Pages for a menu: the layout's page list resolved against the CMS
   sections, plus a trailing page per unplaced section so nothing in the
   CMS silently disappears from the PDF. */
export function paginate(menus, type, layout) {
  const byKey = sectionMap(menus, type, layout);
  const placed = new Set();
  for (const m of Object.values(layout.menus || {}))
    for (const p of m.pages || []) for (const k of p.sections || []) placed.add(k);
  const pages = (layout.menus?.[type]?.pages || []).map((pageCfg) => ({
    pageCfg,
    secs: (pageCfg.sections || [])
      .map((key) => (byKey.has(key) ? { key, section: byKey.get(key) } : null))
      .filter(Boolean),
  }));
  for (const [key, section] of byKey) {
    if (placed.has(key)) continue;
    pages.push({ pageCfg: { id: `auto-${key}`, sections: [key], free: [], page: {} }, secs: [{ key, section }] });
  }
  return pages;
}

function isRecentSection(section, since) {
  if (section.updatedAt && new Date(section.updatedAt).getTime() > since) return true;
  return (section.items || []).some((i) => i.updatedAt && new Date(i.updatedAt).getTime() > since);
}

function MenuPages({ menus, type, layout, assets, recentSince }) {
  const ed = useEditor();
  let pages = paginate(menus, type, layout);
  if (recentSince) pages = pages.filter(({ secs }) => secs.some((s) => isRecentSection(s.section, recentSince)));
  // Print skips empty pages; the designer keeps them as drop targets.
  if (!ed) pages = pages.filter(({ secs }) => secs.length > 0);
  if (pages.length === 0) return null;
  return pages.map(({ pageCfg, secs }, i) => (
    <Page key={pageCfg.id || i} menu={type} pageIndex={i} pageCfg={pageCfg} secs={secs} layout={layout} assets={assets} />
  ));
}

// ─── Root ──────────────────────────────────────────────────────────────────

const MenuPrint = forwardRef(function MenuPrint({ menus, layout: layoutProp, assets, selection }, ref) {
  const layout = layoutProp || DEFAULT_LAYOUT;
  if (!menus || !selection?.length) return null;
  const has = (k) => selection.includes(k) || (selection.includes("all") && k !== "recent");
  const rootStyle = {
    "--mp-font": fontCss(layout.theme?.font || "body"),
    "--mp-script": fontCss(layout.theme?.scriptFont || "script"),
    "--mp-ink": layout.theme?.ink || "#111",
    "--mp-paper": layout.theme?.paper || "#fff",
    color: layout.theme?.ink || "#111",
  };

  if (has("recent")) {
    const since = Date.now() - 24 * 60 * 60 * 1000;
    return (
      <div className="mp" ref={ref} style={rootStyle}>
        {MENU_ORDER.filter((m) => m !== "cover").map((m) => (
          <MenuPages key={m} menus={menus} type={m} layout={layout} assets={assets} recentSince={since} />
        ))}
      </div>
    );
  }

  return (
    <div className="mp" ref={ref} style={rootStyle}>
      {(selection.includes("all") || selection.includes("dinner") || selection.includes("cover")) && (
        <CoverPage layout={layout} assets={assets} />
      )}
      {has("wine") && <MenuPages menus={menus} type="wine" layout={layout} assets={assets} />}
      {has("dinner") && <MenuPages menus={menus} type="dinner" layout={layout} assets={assets} />}
      {has("lunch") && <MenuPages menus={menus} type="lunch" layout={layout} assets={assets} />}
    </div>
  );
});

export default MenuPrint;
